/**
 * Strengths & Needs assessment — admin-edit tab on the Client Profile.
 * Singleton record per client; uses PUT to upsert.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import TagInput from "@/components/TagInput";

const TEXT_FIELDS = [
  { k: "communication_strengths", l: "Communication strengths", placeholder: "Verbal, gestures, devices…" },
  { k: "cultural_preferences", l: "Cultural preferences", placeholder: "Food, holidays, traditions…" },
  { k: "spiritual_preferences", l: "Spiritual preferences", placeholder: "Religion, practices…" },
  { k: "physical_needs", l: "Physical needs", placeholder: "Mobility, transfers, ADLs…" },
  { k: "emotional_needs", l: "Emotional needs", placeholder: "Reassurance, validation…" },
  { k: "social_needs", l: "Social needs", placeholder: "Companionship, family contact…" },
  { k: "cognitive_needs", l: "Cognitive needs", placeholder: "Cues, reminders, structure…" },
  { k: "medical_needs", l: "Medical needs", placeholder: "Conditions requiring monitoring…" },
  { k: "safety_needs", l: "Safety needs", placeholder: "Fall risk, supervision needs…" },
  { k: "short_term_goals", l: "Short-term goals" },
  { k: "long_term_goals", l: "Long-term goals" },
];

export default function ClientStrengthsNeedsTab({ clientId }) {
  const [data, setData] = useState({
    strengths: [], interests: [], support_network: [],
    communication_strengths: "", cultural_preferences: "", spiritual_preferences: "",
    physical_needs: "", emotional_needs: "", social_needs: "",
    cognitive_needs: "", medical_needs: "", safety_needs: "",
    short_term_goals: "", long_term_goals: "", notes: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api.get(`/clients/${clientId}/strengths-needs`)
      .then(({ data }) => { if (!cancelled) setData((d) => ({ ...d, ...data })); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/clients/${clientId}/strengths-needs`, data);
      toast.success("Strengths & needs saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-10 text-clinical-subtext">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading…
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="client-strengths-needs-tab">
      <div className="card-surface p-4 space-y-3">
        <h3 className="label-eyebrow">Strengths</h3>
        <TagInput value={data.strengths} onChange={(v) => setData({ ...data, strengths: v })}
          suggestions={["alert", "sociable", "verbal", "ambulatory", "self_feeding", "humor", "patient"]}
          placeholder="Add strengths…" testid="sn-strengths" />
        <h3 className="label-eyebrow">Interests / hobbies</h3>
        <TagInput value={data.interests} onChange={(v) => setData({ ...data, interests: v })}
          suggestions={["music", "gardening", "crosswords", "cooking", "pets", "reading", "tv"]}
          placeholder="Add interests…" testid="sn-interests" />
        <h3 className="label-eyebrow">Support network</h3>
        <TagInput value={data.support_network} onChange={(v) => setData({ ...data, support_network: v })}
          suggestions={["daughter", "son", "spouse", "neighbor", "church", "friend"]}
          placeholder="People in their corner…" testid="sn-support" />
      </div>

      <div className="card-surface p-4 space-y-3">
        {TEXT_FIELDS.map((f) => (
          <div key={f.k}>
            <label htmlFor={`sn-${f.k}`} className="label-eyebrow">{f.l}</label>
            <textarea
              id={`sn-${f.k}`}
              rows={2}
              value={data[f.k] || ""}
              onChange={(e) => setData({ ...data, [f.k]: e.target.value })}
              placeholder={f.placeholder || ""}
              className="input-clinical resize-y mt-1"
              data-testid={`sn-${f.k}`}
            />
          </div>
        ))}
      </div>

      <div className="sticky bottom-0 bg-white pt-3">
        <button type="button" onClick={save} disabled={saving} className="btn-primary"
          data-testid="sn-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save strengths & needs
        </button>
      </div>
    </div>
  );
}
